/* ============================================================
   TSAS Native Side Cart — JavaScript v6 (UpCart V2 Feature Parity + DPO)
   Zero dependencies. Uses Shopify Cart API + Product API.
   
   v6 Changes:
   - DPO (Dynamic Product Options) interception — opens side cart
     instead of redirecting to /cart page
   - Skeleton loading states (instant visual feedback)
   - Discount code input (apply/remove codes from cart)
   - Dynamic checkout buttons support
   - Improved performance & perceived speed
   ============================================================ */

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    freeShippingThreshold: 14900, // $149.00 in cents
    currency: '$',
    cartApiUrl: '/cart.js',
    cartAddUrl: '/cart/add.js',
    cartUpdateUrl: '/cart/update.js',
    cartChangeUrl: '/cart/change.js',
    bulkyTags: ['bulky', 'bulk', 'bulky rolls', 'bulky goods'],
    dangerousTags: ['danger', 'dangerous', 'dangerous goods', 'dg', 'hazardous'],
  };

  // DOM References
  let drawer, overlay, cartItems, cartEmpty, checkoutBtn, cartTotal;
  let shippingText, progressFill, preorderBanner;
  let savingsTotal, savingsAmount;
  let instructionsToggle, instructionsField, cartNote;
  let skeleton;
  let discountInput, discountBtn, discountMessage, discountApplied, discountTag, discountRemove;

  // State
  let cart = null;
  let isUpdating = false;
  let compareAtPriceCache = {}; // variant_id -> compare_at_price in cents
  let productTagsCache = {};   // handle -> array of tags
  let fetchInProgress = {};    // handle -> Promise (prevent duplicate fetches)
  let appliedDiscount = null;  // { code: string }
  let skeletonVisible = true;

  // ============================================================
  // Initialize
  // ============================================================
  function init() {
    drawer = document.getElementById('tsas-side-cart');
    if (!drawer) return;

    overlay = drawer.querySelector('[data-tsas-cart-close]');
    cartItems = document.getElementById('tsas-cart-items');
    cartEmpty = document.getElementById('tsas-cart-empty');
    checkoutBtn = document.getElementById('tsas-checkout-btn');
    cartTotal = document.getElementById('tsas-cart-total');
    shippingText = document.getElementById('tsas-shipping-text');
    progressFill = document.getElementById('tsas-progress-fill');
    preorderBanner = document.getElementById('tsas-preorder-banner');
    savingsTotal = document.getElementById('tsas-savings-total');
    savingsAmount = document.getElementById('tsas-total-savings-amount');
    instructionsToggle = document.getElementById('tsas-instructions-toggle');
    instructionsField = document.getElementById('tsas-instructions-field');
    cartNote = document.getElementById('tsas-cart-note');
    skeleton = document.getElementById('tsas-skeleton');
    discountInput = document.getElementById('tsas-discount-input');
    discountBtn = document.getElementById('tsas-discount-btn');
    discountMessage = document.getElementById('tsas-discount-message');
    discountApplied = document.getElementById('tsas-discount-applied');
    discountTag = document.getElementById('tsas-discount-tag');
    discountRemove = document.getElementById('tsas-discount-remove');

    bindEvents();

    // DPO Redirect Catch: If we landed on /cart from a product page,
    // auto-open the side cart drawer (DPO does a hard redirect to /cart)
    const isCartPage = window.location.pathname === '/cart';
    const cameFromProduct = document.referrer && (
      document.referrer.includes('/products/') ||
      document.referrer.includes('/collections/')
    );
    const shouldAutoOpen = isCartPage && cameFromProduct;

    // Pre-fetch cart data on page load (background, no drawer open)
    fetch(CONFIG.cartApiUrl)
      .then(r => r.json())
      .then(data => {
        cart = data;
        // Update shipping bar immediately (even before product data)
        updateShippingBar(data.total_price, false, false);
        updateCartCount();
        // Hide skeleton since we have data
        hideSkeleton();
        // Render cart immediately if auto-opening
        if (shouldAutoOpen) {
          renderCart();
          setTimeout(() => openCart(), 200);
        }
        // Pre-fetch product data for items already in cart (background)
        fetchProductDataBackground(data.items).then(() => {
          // Re-render once product data is available
          if (isOpen()) {
            renderCart();
          }
        });
      })
      .catch(() => { hideSkeleton(); });
  }

  // ============================================================
  // Event Binding
  // ============================================================
  function bindEvents() {
    // Close buttons
    document.querySelectorAll('[data-tsas-cart-close]').forEach(el => {
      el.addEventListener('click', closeCart);
    });

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && isOpen()) closeCart();
    });

    // Intercept Add to Cart forms
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (form.action && form.action.includes('/cart/add')) {
        e.preventDefault();
        handleAddToCart(form);
      }
    });

    // Intercept AJAX add to cart (for themes using fetch/XHR)
    interceptAjaxAddToCart();

    // Intercept DPO (Dynamic Product Options) redirects to /cart
    interceptDPORedirects();

    // Cart icon clicks
    document.querySelectorAll('a[href="/cart"], [data-cart-toggle], .cart-link, .header-cart-btn, [data-action="toggle-cart"]').forEach(el => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        openCart();
      });
    });

    // Special instructions toggle
    if (instructionsToggle) {
      instructionsToggle.addEventListener('click', () => {
        const isExpanded = instructionsToggle.getAttribute('aria-expanded') === 'true';
        instructionsToggle.setAttribute('aria-expanded', !isExpanded);
        instructionsField.style.display = isExpanded ? 'none' : 'block';
      });
    }

    // Cart note save on blur
    if (cartNote) {
      cartNote.addEventListener('blur', () => {
        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note: cartNote.value })
        });
      });
    }

    // Discount code events
    if (discountBtn) {
      discountBtn.addEventListener('click', applyDiscount);
    }
    if (discountInput) {
      discountInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          applyDiscount();
        }
      });
    }
    if (discountRemove) {
      discountRemove.addEventListener('click', removeDiscount);
    }
  }

  // ============================================================
  // Intercept AJAX Add to Cart
  // ============================================================
  function interceptAjaxAddToCart() {
    const originalFetch = window.fetch;
    window.fetch = function(...args) {
      const url = typeof args[0] === 'string' ? args[0] : (args[0]?.url || '');
      if (url.includes('/cart/add')) {
        return originalFetch.apply(this, args).then(response => {
          if (response.ok) {
            const cloned = response.clone();
            cloned.json().then(() => {
              // Open drawer immediately, then refresh cart data
              openCartAndRefresh();
            });
          }
          return response;
        });
      }
      return originalFetch.apply(this, args);
    };

    const originalOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function(method, url, ...rest) {
      if (url && url.includes('/cart/add')) {
        this.addEventListener('load', function() {
          if (this.status >= 200 && this.status < 300) {
            openCartAndRefresh();
          }
        });
      }
      return originalOpen.call(this, method, url, ...rest);
    };
  }

  // ============================================================
  // DPO (Dynamic Product Options) Interception
  // ============================================================
  function interceptDPORedirects() {
    // Method 1: Override window.location assignments to /cart
    // DPO app often does: window.location.href = '/cart' after adding
    const originalLocationDescriptor = Object.getOwnPropertyDescriptor(window, 'location') ||
      Object.getOwnPropertyDescriptor(Window.prototype, 'location');
    
    // We can't override window.location directly in all browsers,
    // so we intercept the common patterns DPO uses:
    
    // Method 2: Intercept form submissions that target /cart
    // DPO sometimes creates a hidden form and submits it
    const originalSubmit = HTMLFormElement.prototype.submit;
    HTMLFormElement.prototype.submit = function() {
      if (this.action && (this.action.includes('/cart/add') || this.action.endsWith('/cart'))) {
        // If it's an add-to-cart form, handle it ourselves
        if (this.action.includes('/cart/add')) {
          handleAddToCart(this);
          return;
        }
      }
      return originalSubmit.call(this);
    };

    // Method 3: Watch for navigation to /cart and intercept it
    // Override pushState/replaceState for SPA-style redirects
    const originalPushState = history.pushState;
    history.pushState = function(state, title, url) {
      if (url && (url === '/cart' || url.endsWith('/cart'))) {
        // Prevent redirect, open side cart instead
        openCartAndRefresh();
        return;
      }
      return originalPushState.apply(this, arguments);
    };

    // Method 4: MutationObserver to catch DPO's dynamic form injection
    // DPO injects forms dynamically - watch for them
    const observer = new MutationObserver((mutations) => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === 1) {
            // Check for DPO-injected forms
            const forms = node.tagName === 'FORM' ? [node] : (node.querySelectorAll ? node.querySelectorAll('form[action*="/cart/add"]') : []);
            forms.forEach(form => {
              if (form.action && form.action.includes('/cart/add') && !form.dataset.tsasIntercepted) {
                form.dataset.tsasIntercepted = 'true';
                form.addEventListener('submit', (e) => {
                  e.preventDefault();
                  handleAddToCart(form);
                });
              }
            });
          }
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });

    // Method 5: Intercept beforeunload-style redirects
    // Catch window.location = '/cart' by monitoring with a periodic check
    // and by overriding the assign/replace methods
    const originalAssign = window.location.assign;
    const originalReplace = window.location.replace;
    
    try {
      window.location.assign = function(url) {
        if (url && (url === '/cart' || url.endsWith('/cart') || url.includes('/cart?'))) {
          openCartAndRefresh();
          return;
        }
        return originalAssign.call(window.location, url);
      };
      window.location.replace = function(url) {
        if (url && (url === '/cart' || url.endsWith('/cart') || url.includes('/cart?'))) {
          openCartAndRefresh();
          return;
        }
        return originalReplace.call(window.location, url);
      };
    } catch(e) {
      // Some browsers restrict overriding location methods
    }

    // Method 6: Listen for DPO-specific custom events
    // Bold Product Options / DPO fires events after adding to cart
    document.addEventListener('bold-cart-add', () => openCartAndRefresh());
    document.addEventListener('dpo:add-to-cart', () => openCartAndRefresh());
    document.addEventListener('dpo:added', () => openCartAndRefresh());
    window.addEventListener('dpo:add-to-cart', () => openCartAndRefresh());
    
    // Also listen for Shopify's native section rendering event (used by some apps)
    document.addEventListener('cart:item-added', () => openCartAndRefresh());
    document.addEventListener('ajaxProduct:added', () => openCartAndRefresh());
    
    // Method 7: Intercept link clicks to /cart
    document.addEventListener('click', (e) => {
      const link = e.target.closest('a[href="/cart"], a[href*="/cart?"]');
      if (link && !link.closest('[data-tsas-cart-close]')) {
        // Don't intercept if it's specifically a "View Cart" link in the side cart
        if (!link.closest('#tsas-side-cart')) {
          e.preventDefault();
          openCart();
        }
      }
    }, true);
  }

  // ============================================================
  // Cart API - Optimized
  // ============================================================
  
  // Open cart immediately and refresh data in background
  function openCartAndRefresh() {
    // Open drawer INSTANTLY (render with whatever we have cached)
    openCartInstant();
    
    // Then fetch fresh cart data and re-render
    fetch(CONFIG.cartApiUrl)
      .then(r => r.json())
      .then(data => {
        cart = data;
        hideSkeleton();
        // Render immediately with cached product data
        renderCart();
        updateCartCount();
        // Then fetch any missing product data in background
        fetchProductDataBackground(data.items).then(() => {
          // Re-render with full data (savings, badges)
          renderCart();
        });
      })
      .catch(err => console.error('TSAS Cart: fetch error', err));
  }

  // Full fetch (used for qty changes and removes)
  function fetchCart() {
    return fetch(CONFIG.cartApiUrl)
      .then(r => r.json())
      .then(data => {
        cart = data;
        hideSkeleton();
        renderCart();
        updateCartCount();
        // Background fetch for any new product data
        fetchProductDataBackground(data.items).then(() => {
          renderCart();
        });
        return data;
      })
      .catch(err => console.error('TSAS Cart: fetch error', err));
  }

  // ============================================================
  // Skeleton Loading
  // ============================================================
  function showSkeleton() {
    if (skeleton) {
      skeleton.style.display = 'block';
      skeletonVisible = true;
    }
    if (cartEmpty) cartEmpty.style.display = 'none';
  }

  function hideSkeleton() {
    if (skeleton) {
      skeleton.style.display = 'none';
      skeletonVisible = false;
    }
  }

  // ============================================================
  // Discount Code
  // ============================================================
  function applyDiscount() {
    const code = discountInput ? discountInput.value.trim() : '';
    if (!code) return;

    // Disable button during request
    if (discountBtn) {
      discountBtn.disabled = true;
      discountBtn.textContent = '...';
    }

    // Shopify doesn't have a direct "apply discount" API for the cart drawer.
    // We store the code and apply it at checkout via URL parameter.
    // We validate by attempting to fetch the discount endpoint.
    fetch(`/discount/${encodeURIComponent(code)}`, {
      method: 'GET',
      redirect: 'follow'
    })
    .then(response => {
      // Shopify redirects to / after applying discount - this means success
      appliedDiscount = { code: code.toUpperCase() };
      
      // Update UI
      showDiscountSuccess(code.toUpperCase());
      
      // Update checkout URL to include discount
      updateCheckoutUrl();
      
      if (discountBtn) {
        discountBtn.disabled = false;
        discountBtn.textContent = 'Apply';
      }
    })
    .catch(err => {
      showDiscountError('Unable to apply discount code');
      if (discountBtn) {
        discountBtn.disabled = false;
        discountBtn.textContent = 'Apply';
      }
    });
  }

  function removeDiscount() {
    appliedDiscount = null;
    
    // Clear the discount from Shopify's session
    fetch('/discount/clear', { method: 'GET', redirect: 'follow' }).catch(() => {});
    
    // Reset UI
    if (discountApplied) discountApplied.style.display = 'none';
    if (discountMessage) discountMessage.style.display = 'none';
    if (discountInput) {
      discountInput.value = '';
      discountInput.parentElement.style.display = 'flex';
    }
    
    // Reset checkout URL
    updateCheckoutUrl();
  }

  function showDiscountSuccess(code) {
    if (discountMessage) {
      discountMessage.textContent = 'Discount applied!';
      discountMessage.className = 'tsas-side-cart__discount-message tsas-side-cart__discount-message--success';
      discountMessage.style.display = 'block';
      setTimeout(() => { discountMessage.style.display = 'none'; }, 3000);
    }
    if (discountTag) {
      discountTag.textContent = code;
    }
    if (discountApplied) {
      discountApplied.style.display = 'flex';
    }
    // Hide the input form
    if (discountInput) {
      discountInput.value = '';
      discountInput.parentElement.style.display = 'none';
    }
  }

  function showDiscountError(msg) {
    if (discountMessage) {
      discountMessage.textContent = msg;
      discountMessage.className = 'tsas-side-cart__discount-message tsas-side-cart__discount-message--error';
      discountMessage.style.display = 'block';
      setTimeout(() => { discountMessage.style.display = 'none'; }, 4000);
    }
  }

  function updateCheckoutUrl() {
    if (!checkoutBtn) return;
    if (appliedDiscount) {
      checkoutBtn.href = '/checkout?discount=' + encodeURIComponent(appliedDiscount.code);
    } else {
      checkoutBtn.href = '/checkout';
    }
  }

  // ============================================================
  // Fetch Product Data - Background (non-blocking)
  // ============================================================
  function fetchProductDataBackground(items) {
    // Get unique product handles that we haven't cached yet
    const handlesToFetch = [];
    
    items.forEach(item => {
      const handle = item.handle;
      if (handle && !productTagsCache[handle] && !fetchInProgress[handle] && !handlesToFetch.includes(handle)) {
        handlesToFetch.push(handle);
      }
    });

    if (handlesToFetch.length === 0) return Promise.resolve();

    // Fetch in batches of 6 to avoid overwhelming the browser
    const batchSize = 6;
    const batches = [];
    for (let i = 0; i < handlesToFetch.length; i += batchSize) {
      batches.push(handlesToFetch.slice(i, i + batchSize));
    }

    let chain = Promise.resolve();
    batches.forEach(batch => {
      chain = chain.then(() => {
        const fetches = batch.map(handle => {
          // Track in-progress fetches to prevent duplicates
          fetchInProgress[handle] = true;
          return fetch(`/products/${handle}.js`)
            .then(r => r.json())
            .then(product => {
              if (product) {
                productTagsCache[handle] = (product.tags || []).map(t => t.toLowerCase().trim());
                if (product.variants) {
                  product.variants.forEach(variant => {
                    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
                      compareAtPriceCache[variant.id] = variant.compare_at_price;
                    } else {
                      compareAtPriceCache[variant.id] = 0;
                    }
                  });
                }
              }
              delete fetchInProgress[handle];
            })
            .catch(() => {
              productTagsCache[handle] = [];
              delete fetchInProgress[handle];
            });
        });
        return Promise.all(fetches);
      });
    });

    return chain;
  }

  // ============================================================
  // Check product tags for bulky/dangerous
  // ============================================================
  function isBulky(handle) {
    const tags = productTagsCache[handle] || [];
    return tags.some(tag => CONFIG.bulkyTags.includes(tag));
  }

  function isDangerous(handle) {
    const tags = productTagsCache[handle] || [];
    return tags.some(tag => CONFIG.dangerousTags.includes(tag));
  }

  // ============================================================
  // Add to Cart Handler
  // ============================================================
  function handleAddToCart(form) {
    const formData = new FormData(form);
    const data = {};
    
    const idInput = form.querySelector('[name="id"]');
    if (idInput) data.id = idInput.value;
    
    const qtyInput = form.querySelector('[name="quantity"]');
    data.quantity = qtyInput ? parseInt(qtyInput.value) || 1 : 1;

    const properties = {};
    formData.forEach((value, key) => {
      if (key.startsWith('properties[')) {
        const propName = key.replace('properties[', '').replace(']', '');
        properties[propName] = value;
      }
    });
    if (Object.keys(properties).length > 0) data.properties = properties;

    // Open drawer IMMEDIATELY (before the add request completes)
    openCartInstant();

    fetch(CONFIG.cartAddUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    })
    .then(r => {
      if (!r.ok) throw new Error('Add to cart failed');
      return r.json();
    })
    .then(() => {
      // Fetch fresh cart and render
      return fetch(CONFIG.cartApiUrl).then(r => r.json());
    })
    .then(data => {
      cart = data;
      hideSkeleton();
      renderCart();
      updateCartCount();
      resetFormButton(form);
      // Background fetch for product data
      fetchProductDataBackground(data.items).then(() => {
        renderCart();
      });
    })
    .catch(err => {
      console.error('TSAS Cart: add error', err);
      resetFormButton(form);
      form.submit();
    });
  }

  // Reset the add-to-cart button after our JS handles the submission
  function resetFormButton(form) {
    const btn = form.querySelector('[type="submit"], .product-add-to-cart-button, .add-to-cart, button[name="add"]');
    if (btn) {
      btn.removeAttribute('disabled');
      btn.removeAttribute('aria-disabled');
      btn.classList.remove('is-loading', 'btn--loading', 'loading', 'is-added');
      // If the button has a data-add-to-cart-text or similar, restore it
      const btnText = btn.querySelector('.btn-text, .product-add-to-cart-button-text, [data-add-to-cart-text]');
      if (btnText && btnText.getAttribute('data-add-to-cart-text')) {
        btnText.textContent = btnText.getAttribute('data-add-to-cart-text');
      }
    }
  }

  function updateQuantity(key, quantity) {
    if (isUpdating) return;
    isUpdating = true;

    const itemEl = document.querySelector(`[data-cart-item-key="${key}"]`);
    if (itemEl) itemEl.classList.add('tsas-side-cart__item--loading');

    fetch(CONFIG.cartChangeUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
    .then(r => r.json())
    .then(data => {
      cart = data;
      renderCart();
      updateCartCount();
      isUpdating = false;
    })
    .catch(err => {
      console.error('TSAS Cart: update error', err);
      isUpdating = false;
      fetchCart();
    });
  }

  function removeItem(key) {
    updateQuantity(key, 0);
  }

  // ============================================================
  // Rendering
  // ============================================================
  function renderCart() {
    if (!cart || !cartItems) return;

    const items = cart.items || [];
    
    if (items.length === 0) {
      cartItems.innerHTML = '';
      cartItems.appendChild(cartEmpty);
      cartEmpty.style.display = 'flex';
      if (savingsTotal) savingsTotal.style.display = 'none';
      if (checkoutBtn) checkoutBtn.style.pointerEvents = 'none';
      updateShippingBar(0, false, false);
      if (cartTotal) cartTotal.textContent = '$0.00';
      removeShippingWarning();
      return;
    }

    cartEmpty.style.display = 'none';
    if (checkoutBtn) checkoutBtn.style.pointerEvents = 'auto';

    let html = '';
    let totalSavings = 0;
    let hasPreorder = false;
    let hasBulky = false;
    let hasDangerous = false;

    items.forEach(item => {
      // Get compare_at_price from our cache (in cents)
      const comparePrice = compareAtPriceCache[item.variant_id] || 0;
      const price = item.final_price || item.price;
      const itemSaving = comparePrice > price ? (comparePrice - price) * item.quantity : 0;
      totalSavings += itemSaving;

      // Check for pre-order
      const isPreorder = (item.properties && (item.properties._preorder || item.properties.Type === 'Pre-order')) ||
                         (item.variant_title && item.variant_title.toLowerCase().includes('pre-order'));
      if (isPreorder) hasPreorder = true;

      // Check for bulky/dangerous
      const itemIsBulky = isBulky(item.handle);
      const itemIsDangerous = isDangerous(item.handle);
      if (itemIsBulky) hasBulky = true;
      if (itemIsDangerous) hasDangerous = true;

      // Build variant info from options
      let variantInfo = '';
      if (item.variant_title && item.variant_title !== 'Default Title') {
        const options = item.variant_title.split(' / ');
        const optionNames = item.options_with_values || [];
        options.forEach((opt, i) => {
          const name = optionNames[i] ? optionNames[i].name : (i === 0 ? 'Size' : 'Option');
          variantInfo += `<div class="tsas-side-cart__item-variant">${name}: ${opt}</div>`;
        });
      }

      // Build line item properties (DPO custom options)
      let propertiesHtml = '';
      if (item.properties && Object.keys(item.properties).length > 0) {
        Object.entries(item.properties).forEach(([key, value]) => {
          // Skip hidden properties (start with _) and empty values
          if (key.startsWith('_') || !value || value === '') return;
          // Skip properties that are URLs (image uploads etc)
          if (typeof value === 'string' && (value.startsWith('http://') || value.startsWith('https://'))) return;
          // Skip 'Type: Pre-order' since it's rendered separately by the pre-order label
          if (key.toLowerCase() === 'type' && String(value).toLowerCase().includes('pre-order')) return;
          propertiesHtml += `<div class="tsas-side-cart__item-property"><strong>${escapeHtml(key)}:</strong> ${escapeHtml(String(value))}</div>`;
        });
      }

      // Build badges
      let badgesHtml = '';
      if (itemIsBulky) {
        badgesHtml += '<span class="tsas-side-cart__item-badge tsas-side-cart__item-badge--bulky">Bulky Goods</span>';
      }
      if (itemIsDangerous) {
        badgesHtml += '<span class="tsas-side-cart__item-badge tsas-side-cart__item-badge--dangerous">Dangerous Goods</span>';
      }

      html += `
        <div class="tsas-side-cart__item" data-cart-item-key="${item.key}">
          <div class="tsas-side-cart__item-image">
            <a href="${item.url}">
              <img src="${getSizedImage(item.image, '160x')}" alt="${escapeHtml(item.title)}" loading="lazy">
            </a>
          </div>
          <div class="tsas-side-cart__item-details">
            <h3 class="tsas-side-cart__item-title">${escapeHtml(item.product_title)}</h3>
            ${variantInfo}
            ${propertiesHtml}
            ${isPreorder ? '<div class="tsas-side-cart__item-preorder">Type: Pre-order</div>' : ''}
            ${badgesHtml}
            <div class="tsas-side-cart__item-bottom">
              <div class="tsas-side-cart__qty">
                <button type="button" aria-label="Decrease quantity" onclick="window.tsasCart.updateQty('${item.key}', ${item.quantity - 1})">−</button>
                <input type="number" value="${item.quantity}" min="0" aria-label="Quantity" 
                  onchange="window.tsasCart.updateQty('${item.key}', parseInt(this.value) || 0)">
                <button type="button" aria-label="Increase quantity" onclick="window.tsasCart.updateQty('${item.key}', ${item.quantity + 1})">+</button>
              </div>
              <div class="tsas-side-cart__item-pricing">
                ${comparePrice > price ? `<div class="tsas-side-cart__item-compare">${formatMoney(comparePrice * item.quantity)}</div>` : ''}
                <div class="tsas-side-cart__item-price">${formatMoney(price * item.quantity)}</div>
                ${itemSaving > 0 ? `<div class="tsas-side-cart__item-saving">(Save ${formatMoney(itemSaving)})</div>` : ''}
              </div>
            </div>
            <button type="button" class="tsas-side-cart__item-remove" aria-label="Remove item" 
              onclick="window.tsasCart.remove('${item.key}')">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14"/>
              </svg>
            </button>
          </div>
        </div>
      `;
    });

    cartItems.innerHTML = html;

    // Update totals
    if (cartTotal) cartTotal.textContent = formatMoney(cart.total_price);

    // Update savings
    if (totalSavings > 0 && savingsTotal) {
      savingsTotal.style.display = 'flex';
      savingsAmount.textContent = formatMoney(totalSavings);
    } else if (savingsTotal) {
      savingsTotal.style.display = 'none';
    }

    // Update shipping bar (pass bulky/dangerous flags)
    updateShippingBar(cart.total_price, hasBulky, hasDangerous);

    // Update pre-order banner
    if (preorderBanner) {
      preorderBanner.style.display = hasPreorder ? 'block' : 'none';
    }

    // Update cart note
    if (cartNote && cart.note) {
      cartNote.value = cart.note;
    }
  }

  function updateShippingBar(totalCents, hasBulky, hasDangerous) {
    if (!shippingText || !progressFill) return;

    const threshold = CONFIG.freeShippingThreshold;
    const remaining = threshold - totalCents;

    // Show/hide shipping warning for bulky/dangerous goods
    if (hasBulky || hasDangerous) {
      showShippingWarning(hasBulky, hasDangerous);
    } else {
      removeShippingWarning();
    }

    if (remaining <= 0 || totalCents === 0) {
      if (totalCents > 0) {
        if (hasBulky || hasDangerous) {
          shippingText.innerHTML = '<strong>Free shipping does not apply to flagged items below</strong>';
          progressFill.style.width = '100%';
          progressFill.style.background = '#ffc107';
        } else {
          shippingText.innerHTML = '<strong>Free shipping unlocked!</strong>';
          progressFill.style.width = '100%';
          progressFill.style.background = '#4CAF50';
        }
      } else {
        shippingText.innerHTML = `Spend <strong>${formatMoney(threshold)}</strong> for FREE shipping!`;
        progressFill.style.width = '0%';
        progressFill.style.background = '#4CAF50';
      }
    } else {
      const pct = Math.min(((threshold - remaining) / threshold) * 100, 100);
      shippingText.innerHTML = `Spend <strong>${formatMoney(remaining)}</strong> more for FREE shipping!`;
      progressFill.style.width = pct + '%';
      progressFill.style.background = '#4CAF50';
    }

    // Excludes text
    const excludesEl = document.getElementById('tsas-shipping-excludes');
    if (excludesEl) {
      excludesEl.innerHTML = '<a href="/policies/shipping-policy" style="color:#e53935;font-size:12px;">Excludes Bulky & Dangerous Goods</a>';
    }
  }

  function showShippingWarning(hasBulky, hasDangerous) {
    let warningEl = document.getElementById('tsas-shipping-warning');
    if (!warningEl) return;
    let messages = [];
    if (hasBulky) messages.push('Bulky Goods');
    if (hasDangerous) messages.push('Dangerous Goods');
    warningEl.innerHTML = `<strong>⚠️ Your cart contains ${messages.join(' & ')}.</strong> Free shipping excludes these items — additional freight charges may apply at checkout.`;
    warningEl.style.display = 'block';
  }

  function removeShippingWarning() {
    const warningEl = document.getElementById('tsas-shipping-warning');
    if (warningEl) warningEl.style.display = 'none';
  }

  function updateCartCount() {
    const count = cart ? cart.item_count : 0;
    document.querySelectorAll('[data-cart-count], .cart-count, .cart-link__count, .header-cart-count').forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? '' : 'none';
    });
  }

  // ============================================================
  // Open / Close — INSTANT
  // ============================================================
  function openCartInstant() {
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('tsas-cart-open');
    // Show skeleton if we don't have cart data yet
    if (!cart) {
      showSkeleton();
    } else {
      hideSkeleton();
      renderCart();
      // Also force shipping bar update immediately
      updateShippingBar(cart.total_price, false, false);
    }
  }

  function openCart() {
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('tsas-cart-open');
    
    // Show skeleton briefly while fetching
    if (!cart) showSkeleton();
    
    // Fetch fresh cart data (for cart icon click)
    fetch(CONFIG.cartApiUrl)
      .then(r => r.json())
      .then(data => {
        cart = data;
        hideSkeleton();
        renderCart();
        updateCartCount();
        fetchProductDataBackground(data.items).then(() => renderCart());
      })
      .catch(err => {
        hideSkeleton();
        console.error('TSAS Cart: fetch error', err);
      });
  }

  function closeCart() {
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('tsas-cart-open');
  }

  function isOpen() {
    return drawer && drawer.getAttribute('aria-hidden') === 'false';
  }

  // ============================================================
  // Utilities
  // ============================================================
  function formatMoney(cents) {
    if (typeof cents !== 'number') cents = parseInt(cents) || 0;
    const dollars = (cents / 100).toFixed(2);
    return CONFIG.currency + dollars;
  }

  function getSizedImage(url, size) {
    if (!url) return 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg"/>';
    if (url.includes('.jpg') || url.includes('.png') || url.includes('.webp') || url.includes('.gif')) {
      return url.replace(/_(pico|icon|thumb|small|compact|medium|large|grande|original|1024x1024|2048x2048|master)+\./, '.')
               .replace(/\.(jpg|png|webp|gif)/, function(match) {
                 return '_' + size + match;
               });
    }
    return url;
  }

  function escapeHtml(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  // ============================================================
  // Public API
  // ============================================================
  window.tsasCart = {
    open: openCart,
    close: closeCart,
    refresh: fetchCart,
    updateQty: updateQuantity,
    remove: removeItem,
    applyDiscount: applyDiscount,
    removeDiscount: removeDiscount,
  };

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
